import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConversationMessages, simulateAiMessage, clearAiHistory, getEmulatedContacts, createAiContact, deleteEmulatedContact } from '../services/apiContacts';
import { useAuthStore } from '../store/useAuthStore';
import echo from '../lib/echo';
import Header from '../components/layout/Header';
import dayjs from 'dayjs';
import { resolveMediaUrl } from '../lib/utils';

export default function AiSandbox() {
    const { user } = useAuthStore();
    const queryClient = useQueryClient();
    const [activeContact, setActiveContact] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [isSimulating, setIsSimulating] = useState(false);
    const [isAiTyping, setIsAiTyping] = useState(false);

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    const tenantId = user?.tenant_id;

    // Reset selected file and preview when contact changes
    useEffect(() => {
        setSelectedFile(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setNewMessage('');
    }, [activeContact]);

    // Borrado de contactos de prueba. El backend rechaza (422) los que tambien
    // tienen conversaciones reales: createContact reutiliza por telefono, asi
    // que se puede haber emulado sobre un cliente de verdad.
    const deleteContactMutation = useMutation({
        mutationFn: deleteEmulatedContact,
        onSuccess: (data, contactId) => {
            if (activeContact?.id === contactId) setActiveContact(null);
            queryClient.invalidateQueries({ queryKey: ['emulated-contacts', tenantId] });
            const d = data?.deleted || {};
            alert(`Contacto eliminado.

Mensajes: ${d.mensajes ?? 0}
Conversaciones: ${d.conversaciones ?? 0}
Evaluaciones: ${d.evaluaciones ?? 0}`);
        },
        onError: (err) => {
            alert(err?.response?.data?.message || 'No se pudo eliminar el contacto.');
        },
    });

    const handleDeleteContact = (e, contact) => {
        // El item es un <button>: sin esto, borrar tambien lo selecciona.
        e.stopPropagation();
        const nombre = contact.first_name || contact.phone;
        if (!confirm(`Se eliminara "${nombre}" con todos sus mensajes y evaluaciones. Esta accion no se puede deshacer.

Continuar?`)) return;
        deleteContactMutation.mutate(contact.id);
    };

    // Fetch emulated contacts
    const { data: emulatedContacts = [], isLoading: contactsLoading } = useQuery({
        queryKey: ['emulated-contacts', tenantId],
        queryFn: () => getEmulatedContacts(tenantId),
        enabled: !!tenantId,
    });

    const activeConversationId = activeContact?.conversations?.[0]?.id;

    // Fetch messages
    const { data: messagesResponse, isLoading: msgsLoading } = useQuery({
        queryKey: ['messages', activeConversationId],
        queryFn: () => getConversationMessages(activeConversationId),
        enabled: !!activeConversationId,
        refetchInterval: isAiTyping ? 1000 : 5000, // Refetch faster if we expect AI to respond
    });
    
    const messages = messagesResponse?.data || [];

    const scrollToBottom = (behavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isAiTyping]);

    // Echo listener for real-time
    useEffect(() => {
        if (!activeConversationId) return;
        const channelName = `conversations.${activeConversationId}`;
        let channel;
        try {
            channel = echo.private(channelName);
            console.log("Subscribing to channel:", channelName);
            
            channel.listen('.App\\Events\\MessageCreated', (data) => {
                console.log("Real-time message received in Sandbox:", data);
                queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
                setIsAiTyping(false); // Stop typing if we got a message
            });
        } catch (err) { console.warn('Echo error:', err); }
        return () => { if (channel) echo.leave(channelName); };
    }, [activeConversationId, queryClient]);

    const simulateMutation = useMutation({
        mutationFn: simulateAiMessage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
            setIsSimulating(false);
            // setNewMessage(''); // Se limpia inmediatamente en handleSend para permitir multimensajes
            setIsAiTyping(true); // AI starts "thinking" after our message
            
            // Auto-stop typing after 30s as fallback
            setTimeout(() => setIsAiTyping(false), 30000);
        },
        onError: () => {
            setIsSimulating(false);
            alert("Error enviando mensaje.");
        }
    });

    const createContactMutation = useMutation({
        mutationFn: createAiContact,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['emulated-contacts', tenantId] });
            setActiveContact(data.contact);
            alert("Contacto de prueba '" + data.contact.first_name + "' creado.");
        }
    });

    const clearMutation = useMutation({
        mutationFn: clearAiHistory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
        }
    });

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && (file.type.startsWith('image/') || file.type.startsWith('audio/'))) {
            setSelectedFile(file);
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(URL.createObjectURL(file));
        } else if (file) {
            alert("Por favor selecciona solo archivos de imagen o de audio.");
        }
    };

    const handleSend = () => {
        const body = newMessage.trim();
        if (!body && !selectedFile) return;
        if (!activeContact) return;
        
        setNewMessage(''); // Limpiar inmediatamente para permitir escribir el siguiente
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        const fileToSend = selectedFile;
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        
        setIsSimulating(true);
        if (fileToSend) {
            const formData = new FormData();
            formData.append('contact_id', activeContact.id);
            formData.append('file', fileToSend);
            if (body) {
                formData.append('body', body);
            }
            simulateMutation.mutate(formData);
        } else {
            simulateMutation.mutate({
                contact_id: activeContact.id,
                body: body
            });
        }
    };

    const handleCreateTestContact = () => {
        const name = prompt("Nombre del contacto de prueba:", "Tester Virtual");
        if (!name) return;
        const phone = prompt("Número (ej: 12345678):", "549" + Math.floor(Math.random() * 100000000));
        if (!phone) return;

        createContactMutation.mutate({
            phone,
            pushName: name,
            tenant_id: tenantId
        });
    };

    return (
        <div className="flex flex-col h-full bg-slate-900 overflow-hidden text-slate-100">
            <Header title="AI Training Sandbox" />
            
            <div className="flex flex-1 overflow-hidden p-4 md:p-6 gap-6">
                
                {/* Scenario List */}
                <div className="hidden md:flex w-72 flex-col bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
                    <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-800/50">
                        <h3 className="font-bold text-[10px] uppercase tracking-widest text-slate-500">Contactos de Prueba</h3>
                        <button 
                            onClick={handleCreateTestContact}
                            className="bg-primary/20 text-primary hover:bg-primary/30 p-1.5 rounded-lg transition-all"
                        >
                            <span className="material-symbols-outlined text-[20px]">person_add</span>
                        </button>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {contactsLoading ? (
                            <div className="p-4 text-center text-slate-500 text-xs animate-pulse">Cargando escenarios...</div>
                        ) : emulatedContacts.length === 0 ? (
                            <div className="p-6 text-center text-slate-500 flex flex-col gap-3">
                                <span className="material-symbols-outlined text-3xl opacity-20">science</span>
                                <p className="text-[10px] leading-relaxed">No hay conversaciones simuladas.</p>
                            </div>
                        ) : (
                            emulatedContacts.map(contact => (
                                <button
                                    key={contact.id}
                                    onClick={() => setActiveContact(contact)}
                                    className={`w-full p-3 flex gap-3 text-left rounded-xl transition-all group
                                        ${activeContact?.id === contact.id ? 'bg-primary/10 border-primary/30 ring-1 ring-primary/20' : 'hover:bg-slate-700/50 border-transparent'}
                                        border
                                    `}
                                >
                                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center font-bold text-slate-400 group-hover:scale-110 transition-transform">
                                        {contact.first_name?.[0]?.toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-xs truncate">{contact.first_name}</p>
                                        <p className="text-[10px] text-slate-500 font-mono">{contact.phone}</p>
                                    </div>
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        title="Eliminar contacto de prueba y todo su historial"
                                        onClick={(e) => handleDeleteContact(e, contact)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDeleteContact(e, contact); }}
                                        className="shrink-0 self-center text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-base">delete</span>
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Simulator Area */}
                <div className="flex-1 flex flex-col bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden relative">
                    {!activeContact ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-6">
                            <div className="w-24 h-24 rounded-full bg-slate-900 flex items-center justify-center border border-slate-700/50 shadow-inner">
                                <span className="material-symbols-outlined text-5xl opacity-20">science</span>
                            </div>
                            <div className="text-center">
                                <h3 className="text-sm font-bold text-slate-400 mb-1">Entorno de Entrenamiento</h3>
                                <p className="text-xs text-slate-600 max-w-xs">Selecciona un contacto o crea uno nuevo para empezar a interactuar con la IA.</p>
                            </div>
                            <button onClick={handleCreateTestContact} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs font-bold transition">Crear Primer Contacto</button>
                        </div>
                    ) : (
                        <>
                            {/* Chat Header */}
                            <div className="px-6 py-4 bg-slate-800/90 border-b border-slate-700 backdrop-blur-md flex justify-between items-center z-10 shadow-sm">
                                <div className="flex items-center gap-4">
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)] animate-pulse"></div>
                                    <div>
                                        <h2 className="font-bold text-sm text-slate-100">{activeContact.first_name} <span className="text-[10px] text-slate-500 font-mono ml-2">({activeContact.phone})</span></h2>
                                        <p className="text-[9px] text-primary font-bold uppercase tracking-widest mt-0.5">WhatsApp Simulator • Direct AI Link</p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => confirm("¿Resetear historial?") && clearMutation.mutate(activeContact.id)}
                                        className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition"
                                        title="Resetear Conversación"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">refresh</span>
                                    </button>
                                </div>
                            </div>

                            {/* Messages Container */}
                            <div className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-6 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] scroll-smooth">
                                {msgsLoading && messages.length === 0 ? (
                                    <div className="flex-1 flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div></div>
                                ) : messages.length === 0 ? (
                                    <div className="mx-auto bg-slate-900/60 backdrop-blur px-6 py-4 rounded-2xl border border-slate-700/50 text-center max-w-sm shadow-2xl">
                                        <span className="material-symbols-outlined text-primary text-3xl mb-2 block">robot_2</span>
                                        <p className="text-xs text-slate-400 leading-relaxed font-light">Listo para el entrenamiento. Envía un mensaje como si fueras el cliente para ver la respuesta del agente.</p>
                                    </div>
                                ) : (
                                    messages.map((msg) => (
                                        <div key={msg.id} className={`flex ${msg.direction === 'inbound' ? 'justify-start' : 'justify-end'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
                                            <div className={`max-w-[80%] rounded-2xl p-4 shadow-xl border ${
                                                msg.direction === 'inbound' 
                                                    ? 'bg-slate-700 border-slate-600 rounded-bl-none text-slate-100' 
                                                    : (msg.is_ai_generated 
                                                        ? 'bg-primary/10 border-primary/30 text-primary-light rounded-br-none ring-1 ring-primary/20' 
                                                        : 'bg-slate-100 text-slate-900 rounded-br-none border-white shadow-white/5')
                                            }`}>
                                                <div className="flex items-center gap-2 mb-2 opacity-60">
                                                    <span className="text-[9px] font-black uppercase tracking-tighter">
                                                        {msg.direction === 'inbound' ? 'Cliente' : (msg.is_ai_generated ? 'AI Agent' : 'Vendedor')}
                                                    </span>
                                                    {msg.is_ai_generated && <span className="bg-primary text-white text-[8px] px-1 rounded font-bold">LIVE</span>}
                                                </div>
                                                {msg.type === 'image' && msg.media_url && (
                                                    <div className="mb-2 rounded-xl overflow-hidden border border-slate-700/50 max-w-xs shadow-md">
                                                        <img 
                                                            src={resolveMediaUrl(msg.media_url)} 
                                                            alt="Imagen simulada" 
                                                            className="max-w-full max-h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                                                            onClick={() => window.open(resolveMediaUrl(msg.media_url), '_blank')} 
                                                        />
                                                    </div>
                                                )}
                                                {(msg.type === 'voice_note' || msg.type === 'audio') && msg.media_url && (
                                                    <div className="mb-2 rounded-xl overflow-hidden p-3 bg-slate-900/60 border border-slate-700/30 max-w-xs shadow-md flex flex-col gap-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="material-symbols-outlined text-primary text-xl">mic</span>
                                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Nota de Voz</span>
                                                        </div>
                                                        <audio src={resolveMediaUrl(msg.media_url)} controls className="w-full h-8" />
                                                    </div>
                                                )}
                                                {msg.body && (
                                                    <p className="text-sm leading-relaxed whitespace-pre-wrap font-medium">{msg.body}</p>
                                                )}
                                                <div className="flex justify-end mt-2 opacity-30 text-[9px] font-mono">
                                                    {dayjs(msg.created_at).format('HH:mm:ss')}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}

                                {isAiTyping && (
                                    <div className="flex justify-end animate-pulse">
                                        <div className="bg-primary/5 border border-primary/20 rounded-2xl rounded-br-none p-3 flex items-center gap-2">
                                            <div className="flex gap-1">
                                                <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                                <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                                <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"></div>
                                            </div>
                                            <span className="text-[10px] font-bold text-primary uppercase tracking-widest">AI está escribiendo...</span>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Message Composer */}
                            <div className="p-6 bg-slate-800/50 border-t border-slate-700 backdrop-blur-sm">
                                <div className="max-w-4xl mx-auto flex flex-col gap-3">
                                    {/* Preview Area */}
                                    {previewUrl && selectedFile && (
                                        <div className="relative align-self-start self-start bg-slate-900 border border-slate-700 rounded-xl p-3 flex flex-col items-center justify-center shadow-2xl group max-w-[300px] gap-2">
                                            {selectedFile.type.startsWith('image/') ? (
                                                <img
                                                    src={previewUrl}
                                                    alt="Preview"
                                                    className="max-h-24 rounded-lg object-contain"
                                                />
                                            ) : (
                                                <div className="flex flex-col items-center gap-2 p-2">
                                                    <span className="material-symbols-outlined text-4xl text-primary animate-pulse">mic</span>
                                                    <audio src={previewUrl} controls className="max-w-[240px] h-8" />
                                                </div>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setSelectedFile(null);
                                                    URL.revokeObjectURL(previewUrl);
                                                    setPreviewUrl(null);
                                                }}
                                                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 shadow-md transition-colors flex items-center justify-center"
                                                title="Eliminar archivo"
                                            >
                                                <span className="material-symbols-outlined text-[16px] block">close</span>
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex gap-4 items-end">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            accept="image/*,audio/*"
                                            onChange={handleFileSelect}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-14 h-14 bg-slate-700 text-slate-300 hover:text-white hover:bg-slate-600 rounded-2xl flex items-center justify-center transition-all shadow-lg shrink-0"
                                            title="Adjuntar Imagen o Audio"
                                        >
                                            <span className="material-symbols-outlined text-[24px]">attach_file</span>
                                        </button>
                                        
                                        <div className="flex-1 bg-slate-900 border border-slate-700 rounded-2xl p-1 shadow-inner focus-within:border-primary/50 focus-within:ring-4 focus-within:ring-primary/10 transition-all flex items-end">
                                            <textarea
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
                                                placeholder={selectedFile ? "Añadir un comentario..." : "Escribe como cliente..."}
                                                className="w-full bg-transparent border-none text-sm px-4 py-3 outline-none resize-none placeholder:text-slate-600 dark:text-slate-200 min-h-[46px] max-h-40"
                                                rows={1}
                                            />
                                        </div>
                                        <button
                                            onClick={handleSend}
                                            disabled={!newMessage.trim() && !selectedFile}
                                            className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all shadow-2xl relative overflow-hidden group shrink-0
                                                ${(newMessage.trim() || selectedFile) 
                                                    ? 'bg-primary text-white hover:scale-105 active:scale-95' 
                                                    : 'bg-slate-700 text-slate-500 cursor-not-allowed opacity-50'}
                                            `}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                            <span className="material-symbols-outlined text-[24px]">{isSimulating ? 'sync' : 'send'}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Training Stats */}
                <div className="hidden lg:flex w-72 flex-col gap-6">
                    <div className="bg-slate-800 rounded-3xl border border-slate-700 p-6 shadow-2xl flex flex-col gap-6">
                        <div>
                            <h3 className="font-bold text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-6 flex items-center gap-2">
                                <span className="material-symbols-outlined text-[16px] text-primary">data_exploration</span>
                                IA Brain Metrics
                            </h3>
                            <div className="grid grid-cols-1 gap-4">
                                <div className="bg-slate-900 border border-slate-700/50 p-4 rounded-2xl shadow-inner group transition-all hover:border-primary/30">
                                    <p className="text-[9px] text-slate-500 uppercase font-black mb-1.5 opacity-50">Lead Accuracy</p>
                                    <p className="text-2xl font-mono text-primary flex items-baseline gap-1">
                                        {activeContact?.lead_score || '0'}
                                        <span className="text-[10px] text-slate-700">/100</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-bold text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Context Logic</h3>
                            <div className="bg-slate-900 border border-slate-700/50 p-4 rounded-2xl min-h-[120px] shadow-inner text-[11px] text-slate-400 font-medium leading-relaxed italic border-l-4 border-l-primary/30">
                                "{activeContact?.ai_summary || 'Detección de contexto en progreso...'}"
                            </div>
                        </div>
                    </div>

                    <div className="mt-auto p-6 rounded-3xl bg-primary/5 border border-primary/20 shadow-xl">
                        <p className="text-[11px] font-bold text-primary mb-2 flex items-center gap-1">
                            <span className="material-symbols-outlined text-[14px]">lightbulb</span>
                            Tip de Entrenamiento
                        </p>
                        <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                            Puedes resetear el historial si la IA se queda "atrapada" en un contexto anterior. Usa esto para probar cómo responde ante nuevas objeciones o consultas de precios.
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
}
